/**
 * ResidentOpsPanel — S7 Admin integration. Property-scoped resident
 * management + violation review (scope rule) + notification delivery log.
 */
import { useCallback, useEffect, useState } from 'react';
import { ShieldCheck } from 'lucide-react';
import { apiFetch } from '../../lib/apiFetch';
import { toast } from 'sonner';
import ResidentManagementPanel from '../ResidentManagementPanel';
import { HistoryList, HistoryFilters } from '../../pages/resident/ResidentHistory';
import { useAdminScopeTerms } from '../../lib/adminScopeTerms';

function AdminHistory({ propertyId }) {
  const terms = useAdminScopeTerms();
  const [entries, setEntries] = useState([]);
  const [cat, setCat] = useState('all');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [unit, setUnit] = useState('');
  const [residentId, setResidentId] = useState('');
  const [residents, setResidents] = useState([]);

  useEffect(() => {
    if (!propertyId) return;
    setResidentId(''); setUnit('');
    (async () => {
      try {
        const d = await apiFetch(`/api/resident/admin/residents?property_id=${propertyId}`);
        setResidents(d.residents || []);
      } catch (_) { setResidents([]); }
    })();
  }, [propertyId]);

  useEffect(() => {
    if (!propertyId) return;
    const qs = new URLSearchParams({
      property_id: propertyId, category: cat,
      ...(from && { date_from: from }), ...(to && { date_to: to }),
      ...(unit && { unit }),
      ...(residentId && { resident_id: residentId }),
    });
    (async () => {
      try {
        const d = await apiFetch(`/api/resident/admin/history?${qs}`);
        setEntries(d.entries || []);
      } catch (_) { setEntries([]); }
    })();
  }, [propertyId, cat, from, to, unit, residentId]);

  return (
    <div data-testid="admin-history-tab">
      <div className="flex items-end gap-3 flex-wrap">
        <div className="flex-1 min-w-0 basis-full">
          <HistoryFilters cat={cat} setCat={setCat} from={from} setFrom={setFrom} to={to} setTo={setTo} testPrefix="ahist" />
        </div>
        <div className="mb-3">
          <p className="text-[9px] uppercase tracking-widest text-zinc-500 mb-0.5">Unit</p>
          <input placeholder="101A" value={unit} onChange={(e) => setUnit(e.target.value)}
            className="rounded bg-zinc-900 border border-zinc-700 px-2 py-1.5 text-white text-xs w-24" data-testid="ahist-unit" />
        </div>
        <div className="mb-3">
          <p className="text-[9px] uppercase tracking-widest text-zinc-500 mb-0.5">{terms.occupant}</p>
          <select value={residentId} onChange={(e) => setResidentId(e.target.value)}
            className="rounded bg-zinc-900 border border-zinc-700 px-2 py-1.5 text-white text-xs max-w-[200px]"
            data-testid="ahist-resident">
            <option value="">All {terms.occupants}</option>
            {residents.map((r) => (
              <option key={r.user_id} value={r.user_id}>{`${r.name} · ${r.unit_number || '—'}`}</option>
            ))}
          </select>
        </div>
      </div>
      <HistoryList entries={entries} />
    </div>
  );
}

const GOLD = '#C9A962';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const DAYS = [['mon', 'Mon'], ['tue', 'Tue'], ['wed', 'Wed'], ['thu', 'Thu'], ['fri', 'Fri'], ['sat', 'Sat'], ['sun', 'Sun']];

function PickupsAdmin({ propertyId }) {
  const [cfg, setCfg] = useState(null);
  const [agents, setAgents] = useState([]);
  const [logs, setLogs] = useState([]);
  const [filt, setFilt] = useState({ date_from: '', date_to: '', unit: '' });

  const load = useCallback(async () => {
    if (!propertyId) return;
    try {
      const d = await apiFetch(`/api/resident/admin/pickup-config/${propertyId}`);
      setCfg(d.config); setAgents(d.agents || []);
    } catch (_) { /* non-admin */ }
  }, [propertyId]);

  const loadLogs = useCallback(async () => {
    if (!propertyId) return;
    const qs = new URLSearchParams({ property_id: propertyId, ...Object.fromEntries(Object.entries(filt).filter(([, v]) => v)) });
    try {
      const d = await apiFetch(`/api/resident/admin/pickup-logs?${qs}`);
      setLogs(d.requests || []);
    } catch (_) { setLogs([]); }
  }, [propertyId, filt]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadLogs(); }, [loadLogs]);

  const save = async () => {
    try {
      const res = await apiFetch(`/api/resident/admin/pickup-config/${propertyId}`, {
        method: 'PUT', body: { operational_days: cfg.operational_days, valet_agent_id: cfg.valet_agent_id || null } });
      toast.success(`Saved — agent: ${res.valet_agent_name || 'unassigned'}`);
    } catch (e) { toast.error(e.detail || 'Save failed'); }
  };

  if (!cfg) return null;
  const inputCls = 'rounded bg-zinc-800 border border-zinc-600 px-2 py-1 text-white text-xs';

  return (
    <div className="space-y-4" data-testid="admin-pickups-tab">
      <div className="rounded-lg border border-zinc-700 bg-zinc-800/40 p-3">
        <p className="text-[11px] font-black uppercase tracking-widest mb-2" style={{ color: GOLD }}>Operational Days &amp; Valet Agent</p>
        <div className="flex items-center gap-1.5 flex-wrap">
          {DAYS.map(([k, l]) => {
            const on = (cfg.operational_days || []).includes(k);
            return (
              <button key={k} onClick={() => setCfg((c) => ({ ...c, operational_days: on ? c.operational_days.filter((d) => d !== k) : [...c.operational_days, k] }))}
                className="px-2.5 py-1 rounded-full text-[10px] font-black uppercase border"
                style={on ? { backgroundColor: GOLD, color: '#1a1a1a', borderColor: GOLD } : { color: '#71717a', borderColor: '#3f3f46' }}
                data-testid={`pickup-day-${k}`}>
                {l}
              </button>
            );
          })}
          <select value={cfg.valet_agent_id || ''} onChange={(e) => setCfg((c) => ({ ...c, valet_agent_id: e.target.value || null }))}
            className={`${inputCls} ml-2`} data-testid="pickup-agent-select">
            <option value="">Valet agent: unassigned (falls back to Operations)</option>
            {agents.map((a) => <option key={a.user_id} value={a.user_id}>{a.name} ({a.role})</option>)}
          </select>
          <button onClick={save} className="px-3 py-1 rounded-full text-[10px] font-bold" style={{ backgroundColor: GOLD, color: '#1a1a1a' }} data-testid="pickup-config-save">
            Save
          </button>
        </div>
      </div>

      <div className="flex items-end gap-2 flex-wrap">
        <div><p className="text-[9px] uppercase tracking-widest text-zinc-500 mb-0.5">From</p>
          <input type="date" value={filt.date_from} onChange={(e) => setFilt((s) => ({ ...s, date_from: e.target.value }))} className={inputCls} data-testid="pickup-filter-from" /></div>
        <div><p className="text-[9px] uppercase tracking-widest text-zinc-500 mb-0.5">To</p>
          <input type="date" value={filt.date_to} onChange={(e) => setFilt((s) => ({ ...s, date_to: e.target.value }))} className={inputCls} data-testid="pickup-filter-to" /></div>
        <div><p className="text-[9px] uppercase tracking-widest text-zinc-500 mb-0.5">Unit</p>
          <input placeholder="101A" value={filt.unit} onChange={(e) => setFilt((s) => ({ ...s, unit: e.target.value }))} className={`${inputCls} w-20`} data-testid="pickup-filter-unit" /></div>
      </div>

      {logs.length === 0 ? (
        <p className="text-xs text-zinc-500">No pickup requests or QR scans logged.</p>
      ) : (
        <div className="space-y-1" data-testid="admin-pickup-logs">
          {logs.map((r) => (
            <div key={r.request_id} className="flex items-center justify-between gap-2 rounded bg-zinc-800/40 px-2.5 py-1.5 text-[11px]" data-testid={`pickup-log-${r.request_id}`}>
              <span className="text-zinc-300 truncate">
                <span className={`px-1.5 py-0.5 rounded text-[9px] font-black uppercase mr-1.5 ${r.kind === 'recycling' ? 'bg-emerald-950/60 text-emerald-400' : 'bg-zinc-700 text-zinc-300'}`}>{r.kind}</span>
                <span className={`px-1.5 py-0.5 rounded text-[9px] font-black uppercase mr-1.5 ${r.source === 'qr_scan' ? 'bg-purple-950/60 text-purple-400' : 'bg-blue-950/60 text-blue-400'}`}>{r.source === 'qr_scan' ? 'QR Scan' : 'Manual'}</span>
                Unit {r.unit_number} · {r.resident_name}{r.location ? ` · ${r.location}` : ''}{r.preferred_time ? ` · pref ${r.preferred_time}` : ''}
              </span>
              <span className="text-zinc-600 shrink-0">{new Date(r.created_at).toLocaleString()}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ViolationsReview({ propertyId }) {
  const [rows, setRows] = useState([]);
  const load = useCallback(async () => {
    try {
      const d = await apiFetch(`/api/resident/admin/violations${propertyId ? `?property_id=${propertyId}` : ''}`);
      setRows(d.violations || []);
    } catch (_) { setRows([]); }
  }, [propertyId]);
  useEffect(() => { load(); }, [load]);

  const update = async (ref, patch) => {
    try {
      const res = await apiFetch(`/api/resident/admin/violations/${ref}`, { method: 'PATCH', body: patch });
      toast.success(res.resident_notified ? 'Updated — resident notified (outside NECS scope)' : 'Updated — internal only');
      load();
    } catch (e) { toast.error(e.detail || 'Update failed'); }
  };

  if (rows.length === 0) return <p className="text-xs text-zinc-500">No resident violation reports{propertyId ? ' for this property' : ''}.</p>;
  return (
    <div className="space-y-2" data-testid="admin-resident-violations">
      {rows.map((v) => (
        <div key={v.reference} className="rounded-lg border border-zinc-700 bg-zinc-800/40 p-3" data-testid={`arv-${v.reference}`}>
          <div className="flex items-start justify-between gap-2 flex-wrap">
            <div>
              <p className="text-xs font-bold text-white">{v.violation_type}
                <span className="ml-2 font-mono text-[10px]" style={{ color: GOLD }}>{v.reference}</span>
              </p>
              <p className="text-[11px] text-zinc-400 mt-0.5">{v.description}</p>
              {(v.photos || []).length > 0 && (
                <div className="flex gap-2 mt-1.5 flex-wrap" data-testid={`arv-photos-${v.reference}`}>
                  {v.photos.map((p, i) => (
                    <a key={i} href={`${BACKEND_URL}/api/resident/violations/photo?path=${encodeURIComponent(p.path)}`}
                      target="_blank" rel="noreferrer" className="block w-24">
                      <img src={`${BACKEND_URL}/api/resident/violations/photo?path=${encodeURIComponent(p.path)}`}
                        alt={p.label || p.filename} className="h-16 w-24 object-cover rounded border border-zinc-700" loading="lazy" />
                      <span className="block text-[9px] text-zinc-400 mt-0.5 leading-tight" data-testid={`arv-photo-label-${v.reference}-${i}`}>
                        {p.label || p.filename}
                      </span>
                    </a>
                  ))}
                </div>
              )}
              <p className="text-[10px] text-zinc-600 mt-0.5">
                {v.resident_name} · unit {v.unit_number} · {v.property_name}
                {[v.building && `Bldg ${v.building}`, v.floor && `Fl ${v.floor}`, v.area].filter(Boolean).length > 0 &&
                  ` · ${[v.building && `Bldg ${v.building}`, v.floor && `Fl ${v.floor}`, v.area].filter(Boolean).join(', ')}`}
                {' · '}{new Date(v.submitted_at).toLocaleString()}
              </p>
            </div>
            <div className="flex items-center gap-1.5 shrink-0 flex-wrap">
              <select value={v.within_necs_scope === null || v.within_necs_scope === undefined ? '' : String(v.within_necs_scope)}
                onChange={(e) => update(v.reference, { status: v.status, within_necs_scope: e.target.value === 'true' })}
                className="rounded bg-zinc-800 border border-zinc-600 px-1.5 py-1 text-[10px] text-white"
                data-testid={`arv-scope-${v.reference}`}>
                <option value="" disabled>Scope…</option>
                <option value="true">Within NECS scope (internal)</option>
                <option value="false">Outside scope (notify resident)</option>
              </select>
              <select value={v.status} onChange={(e) => update(v.reference, { status: e.target.value })}
                className="rounded bg-zinc-800 border border-zinc-600 px-1.5 py-1 text-[10px] text-white"
                data-testid={`arv-status-${v.reference}`}>
                <option value="submitted">Submitted</option>
                <option value="reviewed">Reviewed</option>
                <option value="in_progress">In Progress</option>
                <option value="resolved">Resolved</option>
              </select>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function DeliveryLog({ propertyId }) {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const d = await apiFetch(`/api/resident/admin/notification-log${propertyId ? `?property_id=${propertyId}` : ''}`);
        setRows(d.log || []);
      } catch (_) { setRows([]); }
    })();
  }, [propertyId]);
  if (rows.length === 0) return <p className="text-xs text-zinc-500">No deliveries logged yet.</p>;
  return (
    <div className="space-y-1" data-testid="admin-delivery-log">
      {rows.slice(0, 50).map((r, i) => (
        <div key={i} className="flex items-center justify-between gap-2 rounded bg-zinc-800/40 px-2.5 py-1.5 text-[11px]">
          <span className="text-zinc-300 truncate">{r.title}</span>
          <span className="flex gap-1 shrink-0">
            {['in_app', 'sms', 'email'].map((ch) => (
              <span key={ch} className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase ${r.delivery?.[ch] ? 'bg-emerald-950/60 text-emerald-400' : 'bg-zinc-800 text-zinc-600'}`}>
                {ch.replace('_', '-')}
              </span>
            ))}
            <span className="text-zinc-600 ml-1">{new Date(r.at).toLocaleString()}</span>
          </span>
        </div>
      ))}
    </div>
  );
}

export default function ResidentOpsPanel() {
  const terms = useAdminScopeTerms();
  const [props, setProps] = useState([]);
  const [propertyId, setPropertyId] = useState('');
  const [tab, setTab] = useState('residents');

  useEffect(() => {
    (async () => {
      try {
        const d = await apiFetch('/api/resident/admin/community-codes');
        setProps(d.properties || []);
        if (d.properties?.[0]) setPropertyId(d.properties[0].property_id);
      } catch (_) { /* non-admin */ }
    })();
  }, []);

  const prop = props.find((p) => p.property_id === propertyId);

  return (
    <section className="rounded-xl border border-zinc-700 bg-zinc-900/70 p-5 mt-6" data-testid="resident-ops-panel">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <h2 className="flex items-center gap-2 text-sm font-black uppercase tracking-[0.2em]" style={{ color: GOLD }}>
          <ShieldCheck className="h-4 w-4 shrink-0" /> {terms.occupant} Accounts &amp; Violations
        </h2>
        <div className="flex flex-wrap items-center gap-2 min-w-0 w-full">
          <select value={propertyId} onChange={(e) => setPropertyId(e.target.value)}
            className="max-w-full min-w-0 rounded bg-zinc-800 border border-zinc-600 px-2 py-1.5 text-white text-xs" data-testid="rop-property-select">
            {props.map((p) => <option key={p.property_id} value={p.property_id}>{p.property_name}</option>)}
          </select>
          <div className="flex flex-wrap max-w-full gap-1 border border-zinc-700 rounded-lg p-0.5">
            {[['residents', 'Residents'], ['violations', 'Violations'], ['pickups', 'Pickups'], ['history', 'History'], ['log', 'Delivery Log']].map(([k, l]) => (
              <button key={k} onClick={() => setTab(k)}
                className="px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider"
                style={tab === k ? { backgroundColor: GOLD, color: '#1a1a1a' } : { color: '#a1a1aa' }}
                data-testid={`rop-tab-${k}`}>
                {terms.text(l)}
              </button>
            ))}
          </div>
        </div>
      </div>
      {tab === 'residents' && <ResidentManagementPanel propertyId={propertyId} propertyName={prop?.property_name} propertyType={prop?.property_type} isAdmin />}
      {tab === 'violations' && <ViolationsReview propertyId={propertyId} />}
      {tab === 'pickups' && <PickupsAdmin propertyId={propertyId} />}
      {tab === 'history' && <AdminHistory propertyId={propertyId} />}
      {tab === 'log' && <DeliveryLog propertyId={propertyId} />}
    </section>
  );
}
